import { useApp } from '../../context/AppContext';
import { ProgressBar } from '../common/ProgressBar';
import { TIER, DIFFICULTY } from '../../data/actions';

function StatCard({ label, value, subtext, color, onClick }) {
  return (
    <div
      onClick={onClick}
      onKeyDown={onClick ? (e) => e.key === 'Enter' && onClick() : undefined}
      tabIndex={onClick ? 0 : undefined}
      role={onClick ? "button" : undefined}
      style={{
        background: "rgba(255,255,255,0.04)",
        border: "1px solid rgba(255,255,255,0.07)",
        borderRadius: "14px",
        padding: "1rem 0.75rem",
        textAlign: "center",
        cursor: onClick ? "pointer" : "default",
        transition: "transform 0.2s, box-shadow 0.2s",
      }}
    >
      <div 
        style={{ 
          fontSize: "1.75rem", 
          fontWeight: "bold", 
          color, 
          fontFamily: "monospace" 
        }}
      >
        {value}
      </div>
      <div style={{ fontSize: "0.68rem", color: "#9ca3af", marginTop: "0.2rem" }}>
        {label}
      </div>
      <div style={{ fontSize: "0.63rem", color: "#6b7280", marginTop: "0.12rem" }}>
        {subtext}
      </div>
    </div>
  );
}

function PriorityAction({ action, onClick }) {
  return (
    <div
      onClick={onClick}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
      tabIndex={0}
      role="button"
      style={{
        display: "flex",
        alignItems: "center",
        gap: "0.75rem",
        background: "rgba(239,68,68,0.05)",
        border: "1px solid rgba(239,68,68,0.18)",
        borderRadius: "12px",
        padding: "0.8rem 1rem",
        marginBottom: "0.55rem",
        cursor: "pointer",
        transition: "transform 0.2s, background 0.2s",
      }}
    >
      <span style={{ fontSize: "1.3rem" }} aria-hidden="true">{action.emoji}</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: "0.88rem", color: "#f0fdf4" }}>{action.title}</div>
        <div style={{ fontSize: "0.7rem", color: "#6b7280", marginTop: "0.1rem" }}>
          ~{action.co2Saved >= 1000 ? `${(action.co2Saved/1000).toFixed(1)}t` : `${action.co2Saved}kg`} CO₂/yr · {TIER.HIGH.label}
          <span style={{ marginLeft: "0.4rem", color: DIFFICULTY[action.difficulty].color }}>
            · {action.difficulty}
          </span>
        </div>
      </div>
      <span style={{ color: "#6b7280", fontSize: "0.65rem" }} aria-hidden="true">→</span>
    </div>
  );
}

export function Home() {
  const { 
    userName, setUserName,
    userNameEdit, setUserNameEdit,
    nameInput, setNameInput,
    doneActions, items, totalCO2Saved, progressPercent,
    footprint, footprintLabel, calculatorComplete,
    showToast, setView, setExpandedAction, setOnboardingStep,
    exportProgress, importProgress, unlockedAchievements
  } = useApp();

  const highPriorityActions = items.filter(a => !a.done && a.tier === "HIGH").slice(0, 3);

  const handleSaveName = () => {
    if (nameInput.trim()) {
      setUserName(nameInput.trim());
      setUserNameEdit(false);
      showToast(`Welcome, ${nameInput.trim()}!`, "🌿");
    }
  };

  return (
    <div style={{ animation: "slideIn 0.3s ease" }}>
      {/* Header */}
      <div style={{ textAlign: "center", padding: "2rem 0 1.5rem" }}>
        <div 
          style={{ 
            fontSize: "0.68rem", 
            letterSpacing: "0.32em", 
            color: "#4ade80", 
            textTransform: "uppercase", 
            marginBottom: "0.6rem" 
          }}
        >
          Evidence-Based · 2025
        </div>
        <h1 style={{ fontSize: "2.5rem", fontWeight: "normal", margin: "0 0 0.4rem", lineHeight: 1.15, color: "#f0fdf4" }}>
          {userName ? `${userName}'s` : "Your"}<br />
          <em style={{ color: "#4ade80" }}>Green Action Plan</em>
        </h1>
        <p style={{ color: "#6b7280", fontSize: "0.88rem", maxWidth: "440px", margin: "0.6rem auto 1.25rem", lineHeight: 1.7 }}>
          Ranked by actual climate science — not what feels good, but what genuinely works.
        </p>

        {/* Name input */}
        {!userName && !userNameEdit && (
          <button
            onClick={() => setUserNameEdit(true)}
            style={{
              background: "rgba(34,197,94,0.1)",
              border: "1px solid rgba(34,197,94,0.25)",
              color: "#86efac",
              padding: "0.4rem 1rem",
              borderRadius: "99px",
              fontSize: "0.78rem",
              cursor: "pointer",
            }}
          >
            👋 Add your name
          </button>
        )}
        {userNameEdit && (
          <div style={{ display: "flex", gap: "0.5rem", justifyContent: "center" }}>
            <input
              autoFocus
              value={nameInput}
              onChange={e => setNameInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleSaveName()}
              placeholder="Your first name..."
              aria-label="Enter your name"
              style={{
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(34,197,94,0.3)",
                color: "#e8f5e9",
                padding: "0.45rem 0.9rem",
                borderRadius: "8px",
                fontSize: "0.83rem",
                outline: "none",
              }}
            />
            <button
              onClick={handleSaveName}
              style={{
                background: "#16a34a",
                border: "none",
                color: "#fff",
                padding: "0.45rem 0.9rem",
                borderRadius: "8px",
                fontSize: "0.8rem",
                cursor: "pointer",
              }}
            >
              Save
            </button>
          </div>
        )}
      </div>

      {/* Stat cards */}
      <div 
        style={{ 
          display: "grid", 
          gridTemplateColumns: "repeat(3,1fr)", 
          gap: "0.65rem", 
          marginBottom: "1.5rem" 
        }}
      >
        <StatCard
          label="Actions Committed"
          value={`${doneActions.length}/${items.length}`}
          subtext={`${progressPercent}% complete`}
          color="#4ade80"
        />
        <StatCard
          label="CO₂ Saved"
          value={totalCO2Saved > 0 ? `${(totalCO2Saved/1000).toFixed(1)}t` : "—"}
          subtext="per year"
          color="#34d399"
        />
        <StatCard
          label="My Footprint"
          value={calculatorComplete ? `${footprint.toFixed(1)}t` : "—"}
          subtext={calculatorComplete ? footprintLabel : "Tap to calculate"}
          color="#c4b5fd"
          onClick={() => setView("calculator")}
        />
      </div>

      {/* Achievement count */}
      {unlockedAchievements.length > 0 && (
        <div 
          style={{
            background: "rgba(255,215,0,0.05)",
            border: "1px solid rgba(255,215,0,0.2)",
            borderRadius: "10px",
            padding: "0.6rem 1rem",
            marginBottom: "1rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.5rem",
          }}
        >
          <span style={{ fontSize: "1.1rem" }}>🏆</span>
          <span style={{ color: "#fbbf24", fontSize: "0.82rem" }}>
            {unlockedAchievements.length} achievement{unlockedAchievements.length !== 1 ? "s" : ""} unlocked!
          </span>
        </div>
      )}

      {/* Progress bar */}
      <div style={{ marginBottom: "1.5rem" }}>
        <ProgressBar percent={progressPercent} />
      </div>

      {/* Top priorities */}
      {highPriorityActions.length > 0 && (
        <>
          <div 
            style={{ 
              fontSize: "0.7rem", 
              letterSpacing: "0.15em", 
              color: "#6b7280", 
              textTransform: "uppercase", 
              marginBottom: "0.65rem" 
            }}
          >
            🔥 Top priorities
          </div>
          {highPriorityActions.map(action => (
            <PriorityAction
              key={action.id}
              action={action}
              onClick={() => {
                setView("actions");
                setExpandedAction(action.id);
              }}
            />
          ))}
        </>
      )}

      {/* All done message */}
      {doneActions.length === items.length && (
        <div 
          style={{ 
            textAlign: "center", 
            padding: "2rem", 
            color: "#4ade80", 
            fontSize: "1.1rem" 
          }}
        >
          🎉 You've committed to all actions! Go share your plan!
        </div>
      )}

      {/* CTA buttons */}
      <div style={{ display: "flex", gap: "0.65rem", marginTop: "1.5rem", flexWrap: "wrap" }}>
        {[
          { label: "📊 Calculate Footprint", view: "calculator", color: "#a78bfa", bg: "rgba(168,85,247,0.12)", border: "rgba(168,85,247,0.25)" },
          { label: "✅ All Actions", view: "actions", color: "#86efac", bg: "rgba(34,197,94,0.12)", border: "rgba(34,197,94,0.25)" },
          { label: "📣 Share Plan", view: "share", color: "#93c5fd", bg: "rgba(59,130,246,0.1)", border: "rgba(59,130,246,0.2)" },
        ].map(button => (
          <button
            key={button.view}
            onClick={() => setView(button.view)}
            style={{
              flex: 1,
              minWidth: "130px",
              background: button.bg,
              border: `1px solid ${button.border}`,
              color: button.color,
              padding: "0.72rem",
              borderRadius: "10px",
              fontSize: "0.82rem",
              cursor: "pointer",
              transition: "transform 0.2s",
            }}
          >
            {button.label}
          </button>
        ))}
      </div>

      {/* Export/Import */}
      <div 
        style={{ 
          marginTop: "1.5rem", 
          background: "rgba(255,255,255,0.02)", 
          border: "1px solid rgba(255,255,255,0.06)", 
          borderRadius: "12px", 
          padding: "0.9rem 1rem" 
        }}
      >
        <div 
          style={{ 
            fontSize: "0.68rem", 
            letterSpacing: "0.15em", 
            color: "#4b5563", 
            textTransform: "uppercase", 
            marginBottom: "0.7rem" 
          }}
        >
          💾 Your data
        </div>
        <div style={{ display: "flex", gap: "0.55rem", flexWrap: "wrap" }}>
          <button
            onClick={exportProgress}
            style={{
              background: "rgba(34,197,94,0.08)",
              border: "1px solid rgba(34,197,94,0.18)",
              color: "#86efac",
              padding: "0.42rem 0.9rem",
              borderRadius: "8px",
              fontSize: "0.76rem",
              cursor: "pointer",
            }}
          >
            ⬇ Export progress
          </button>
          <label
            style={{
              background: "rgba(99,102,241,0.08)",
              border: "1px solid rgba(99,102,241,0.2)",
              color: "#a5b4fc",
              padding: "0.42rem 0.9rem",
              borderRadius: "8px",
              fontSize: "0.76rem",
              cursor: "pointer",
            }}
          >
            ⬆ Import progress
            <input 
              type="file" 
              accept=".json" 
              onChange={importProgress} 
              style={{ display: "none" }} 
              aria-label="Import progress from file"
            />
          </label>
          <button
            onClick={() => setOnboardingStep(0)}
            style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.08)",
              color: "#6b7280",
              padding: "0.42rem 0.9rem",
              borderRadius: "8px",
              fontSize: "0.76rem",
              cursor: "pointer",
            }}
          >
            ? How to use
          </button>
        </div>
      </div>
    </div>
  );
}
